import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import type {FormSelectOptionsList} from 'swayok-react-mdb-ui-kit/types'
import type {AnyObject} from 'swayok-react-mdb-ui-kit/types/Common'

// Настройки хука.
export interface UseLiveSearchLoaderEffectConfig {
    // Включить / выключить функциональность.
    enabled?: boolean
    // Время задержки перед выполнением запроса.
    // Исключает спам API запросами при каждом изменении ключевых слов.
    debounceTimeoutMs?: number
    // Минимальное количество символов в ключевых словах,
    // при котором разрешено выполнять запросы в API.
    minCharsToSend?: number
    // Кол-во опций, которое нужно загрузить.
    limit?: number
    // Выполняется при ошибке загрузки опций из API.
    onError?: (error: ApiError) => void
}

// Возвращаемые данные и функции.
export interface UseLiveSearchLoaderEffectHookReturn<
    OptionValueType = string,
    OptionExtras extends AnyObject = AnyObject,
> {
    isLoading: boolean
    options: FormSelectOptionsList<OptionValueType, OptionExtras>
    // Отменить выполняющийся запрос и сбросить тайм-аут.
    stopPendingRequest: () => void
    // Отменить выполняющийся запрос, сбросить тайм-аут и очистить список опций.
    reset: () => void
    // Запуск загрузки опций через заданный в настройках тайм-аут (debounce).
    onKeywordsChange: (keywords: string) => void
}

// Хук, отвечающий за выполнение запроса к API на получение списка опций-подсказок
// при изменении ключевых слов.
// Комбинируется с <ComboboxInput> для реализации подсказок по мере ввода значения.
export function useLiveSearch<
    OptionValueType = string,
    OptionExtras extends AnyObject = AnyObject,
>(
    loader: (
        keywords: string,
        limit: number,
        abortController: AbortController
    ) => Promise<FormSelectOptionsList<OptionValueType, OptionExtras>>,
    config?: UseLiveSearchLoaderEffectConfig
): UseLiveSearchLoaderEffectHookReturn<OptionValueType, OptionExtras> {
    const {
        enabled = true,
        debounceTimeoutMs = 300,
        minCharsToSend = 3,
        limit = 10,
        onError,
    } = (config ?? {})

    // Список найденных статей быстрого поиска.
    const [
        options,
        setOptions,
    ] = useState<FormSelectOptionsList<OptionValueType, OptionExtras>>([])

    const [
        isLoading,
        setIsLoading,
    ] = useState<boolean>(false)

    // Тайм-аут загрузки данных для быстрого поиска.
    const timeoutIdRef = useRef<number | null>(null)
    // Контроллер отмены запроса быстрого поиска.
    const abortControllerRef = useRef<AbortController | null>(null)
    const lastKeywordsRef = useRef<string>('')

    // Остановить timeout и выполняющийся запрос.
    const stopPendingRequest = useCallback(() => {
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current)
            timeoutIdRef.current = null
        }
        abortControllerRef.current?.abort()
    }, [])

    // Сброс состояния и отмена запроса.
    const reset = useCallback(() => {
        stopPendingRequest()
        lastKeywordsRef.current = ''
        setIsLoading(false)
        setOptions([])
    }, [])

    // Останавливаем запрос опций, если liveSearch был отключен.
    useEffect(() => {
        if (!enabled) {
            reset()
        }
    }, [enabled])

    // Быстрый поиск при изменении keywords.
    const onKeywordsChange = useEventCallback((
        keywords: string
    ) => {
        keywords = keywords.trim()
        if (
            !enabled
            || keywords.length < Math.max(1, minCharsToSend)
        ) {
            // Быстрый поиск запрещен / слишком мало символов.
            reset()
            return
        }
        if (lastKeywordsRef.current === keywords) {
            // Введен пробел в начале или конце.
            return
        }
        // Отменяем предыдущий тайм-аут или запрос.
        stopPendingRequest()
        // Планируем новый запрос через тайм-аут.
        timeoutIdRef.current = window.setTimeout(() => {
            timeoutIdRef.current = null
            const abortController: AbortController = new AbortController()
            abortControllerRef.current = abortController
            lastKeywordsRef.current = keywords
            setIsLoading(true)
            loader(
                keywords,
                Math.max(1, limit),
                abortController
            )
                .then(setOptions)
                .catch((error: ApiError) => {
                    setOptions([])
                    if (error.errorType !== 'abort' && error.errorType !== 'timeout') {
                        onError?.(error)
                    }
                })
                .finally(() => {
                    setIsLoading(false)
                })
        }, Math.max(100, debounceTimeoutMs))
    })

    return {
        onKeywordsChange,
        isLoading,
        options,
        stopPendingRequest,
        reset,
    }
}
